import { useCallback, useMemo } from 'react'

import type { Budget } from '@subaio/api-types/budget'
import type { UserBank } from '@subaio/api-types/psd2'
import { PaymentFrequency } from '@subaio/api-types/shared'
import { useAsyncResource, useRefreshable, useStepHandlers } from '@subaio/components'
import { Brand } from '@subaio/webapp'

import { asContainer } from 'lib/container'

import { CenterContent } from 'components/Layout'
import { StepWizard } from 'components/StepWizard'

import { HeaderWrapper } from './HeaderWrapper'
import { type Step, StepType } from './Step'
import {
	Accounts,
	Authentication,
	BudgetEditForm,
	Expired,
	Introduction,
	MissingCoapplicant,
	Success,
	Terms,
	Unauthorized,
} from './Steps'
import { getStepHandlers } from './stepHandler'

type State = {
	step?: Step
}

type Params = {
	sessionId: string
}

export const container = asContainer<State, Params>()({
	path: '/:sessionId',
	authentication: { type: 'public' },
	initialize: async ({ setUser, sessionId, api }) => ({
		step: await getStepHandlers({
			onLogEvent: () => undefined,
			api,
			setUser,
			sessionId,
		}).onInitial(),
	}),
	Running: ({ step, sessionId, setData, setUser, api, userAuth }) => {
		const { onAuthenticationSubmit, onIntroductionNext, onTermsAccept, onAccountSelectComplete, onBudgetEditFinish } =
			useStepHandlers(
				step,
				useCallback((step) => setData({ step }), [setData]),
				useMemo(
					() =>
						getStepHandlers({
							onLogEvent: () => undefined,
							api,
							setUser,
							sessionId,
						}),
					[sessionId, setUser, api],
				),
			)

		const onGetBudget = useCallback(async () => {
			if (userAuth.status !== 'authenticated') return undefined
			return api.get<Budget | undefined>(`/budgets/${userAuth.budgetId}`)
		}, [api, userAuth])

		const { refreshable, refresh } = useRefreshable()
		const selectedBanksResource = useAsyncResource(
			useCallback(
				async (abortIfCancelled) => {
					if (!step || step.type !== StepType.Accounts) return null
					refreshable()
					if (userAuth.status !== 'authenticated') return null
					const banks = await api.get<Array<UserBank>>('/psd2/selected-banks')
					abortIfCancelled()
					return banks
				},
				[api, userAuth, refreshable, step],
			),
		)

		const urlResource = useAsyncResource(
			useCallback(
				async (abortIfCancelled) => {
					if (!step || step.type !== StepType.Accounts) return null
					refreshable()
					if (userAuth.status !== 'authenticated') return null
					const { url } = await api.post<{ url: string; sessionId: string }>('/psd2/initiate', {})
					abortIfCancelled()

					return url
				},
				[api, userAuth, refreshable, step],
			),
		)

		return (
			<CenterContent>
				<StepWizard<Step>
					step={step}
					header={
						userAuth.status === 'authenticated' ? (
							<HeaderWrapper>
								<Brand type='original' brandId={userAuth.brandId ?? undefined} />
							</HeaderWrapper>
						) : null
					}
					renderers={{
						[StepType.Authentication]: (currentStep) => (
							<Authentication {...currentStep} onSubmit={onAuthenticationSubmit} />
						),
						[StepType.Introduction]: (currentStep) => <Introduction {...currentStep} onNext={onIntroductionNext} />,
						[StepType.Terms]: (currentStep) => (
							<Terms {...currentStep} brandMetadata={currentStep.brandMetadata} onNext={onTermsAccept} />
						),
						[StepType.Accounts]: (currentStep) => (
							<Accounts
								{...currentStep}
								selectedBanksResource={selectedBanksResource}
								urlResource={urlResource}
								onRefresh={refresh}
								onNext={() =>
									onAccountSelectComplete(currentStep.budgetId, currentStep.brandFeatures, currentStep.userMetadata)
								}
							/>
						),
						[StepType.BudgetEditForm]: (currentStep) => (
							<BudgetEditForm
								{...currentStep}
								onAddComment={(comment) => api.post(`/budgets/${currentStep.budgetId}/comment`, { comment })}
								onRecategorizeService={async ({ type, serviceId, categoryId, subCategoryId }) =>
									api.put<Budget>(`/budgets/${currentStep.budgetId}/services/${serviceId}/adjustments`, {
										type,
										categoryId,
										subCategoryId,
									})
								}
								onAdjustCategoryAmount={async ({ categoryId, amount }) => {
									const payload = {
										amount,
										frequency: PaymentFrequency.Monthly,
									}
									// If the amount is undefined, we assume that we want to delete the adjustment
									if (payload.amount === undefined) {
										return api.delete<Budget>(`/budgets/${currentStep.budgetId}/categories/${categoryId}/adjustments`)
									}
									return api.post<Budget>(
										`/budgets/${currentStep.budgetId}/categories/${categoryId}/adjustments`,
										payload,
									)
								}}
								onGetBudget={onGetBudget}
								onSubmitBudget={async () => onBudgetEditFinish(currentStep.budgetId, currentStep.userMetadata)}
							/>
						),
						[StepType.MissingCoapplicant]: (currentStep) => <MissingCoapplicant {...currentStep} />,
						[StepType.Success]: (currentStep) => <Success {...currentStep} />,
						[StepType.Unauthorized]: () => <Unauthorized />,
						[StepType.Expired]: () => <Expired />,
						[StepType.Disabled]: () => null,
					}}
				/>
			</CenterContent>
		)
	},
})
